import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, Chip, Alert, Paper
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

export default function OverrideManagement() {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(false);
  const [revokeDialog, setRevokeDialog] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [form, setForm] = useState({
    user_id: '', productId: '', planId: '', reason: '', expires_at: ''
  });

  useEffect(() => { loadOverrides(); }, []);

  async function loadOverrides() {
    try {
      const data = await billingApi.get('/api/admin/overrides');
      setOverrides(data.overrides);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const body = {
        ...form,
        expires_at: form.expires_at || null
      };
      await billingApi.post('/api/admin/overrides', body);
      setDialog(false);
      setForm({ user_id: '', productId: '', planId: '', reason: '', expires_at: '' });
      loadOverrides();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRevoke() {
    try {
      await billingApi.post(`/api/admin/overrides/${revokeDialog}/revoke`, { reason: revokeReason });
      setRevokeDialog(null);
      setRevokeReason('');
      loadOverrides();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    { field: 'email', headerName: 'User', width: 200 },
    { field: 'product_name', headerName: 'Product', width: 150 },
    { field: 'plan_name', headerName: 'Plan', width: 120 },
    { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 200 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['Active', 'Revoked'],
      valueGetter: ({ row }) => row.is_active ? 'Active' : 'Revoked',
      renderCell: (row) => {
        const label = row.is_active ? 'Active' : 'Revoked';
        return <Chip label={label} color={label === 'Active' ? 'success' : 'default'} size="small" />;
      },
    },
    {
      field: 'expires_at',
      headerName: 'Expires',
      width: 160,
      type: 'date',
      renderCell: (row) => row.expires_at ? formatDate(row.expires_at) : 'Indefinite',
    },
    {
      field: 'created_at',
      headerName: 'Granted',
      width: 160,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.is_active ? (
        <Button size="small" color="error" onClick={() => setRevokeDialog(row.id)}>Revoke</Button>
      ) : null,
    },
  ], []);

  if (loading) return <AdminPageSkeleton />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Admin Overrides</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}>Grant Free Access</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ height: 500 }}>
        <AdminTable
          rows={overrides}
          columns={columns}
          defaultSort={[{ field: 'created_at', direction: 'desc' }]}
          defaultPageSize={25}
        />
      </Paper>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Free Access</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="User ID" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Product ID" value={form.productId} onChange={e => setForm({ ...form, productId: e.target.value })} sx={{ mb: 2 }} helperText="Product UUID or label" />
          <TextField fullWidth label="Plan ID" value={form.planId} onChange={e => setForm({ ...form, planId: e.target.value })} sx={{ mb: 2 }} helperText="e.g., pro, pro-annual" />
          <TextField fullWidth multiline rows={2} label="Reason (required)" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} sx={{ mb: 2 }} />
          <TextField type="datetime-local" fullWidth label="Expires At" InputLabelProps={{ shrink: true }} value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} helperText="Leave empty for indefinite access" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.user_id || !form.productId || !form.planId || !form.reason}>Grant Access</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revokeDialog} onClose={() => setRevokeDialog(null)}>
        <DialogTitle>Revoke Override</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={2} label="Reason" value={revokeReason} onChange={e => setRevokeReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialog(null)}>Cancel</Button>
          <Button color="error" onClick={handleRevoke}>Revoke</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
